import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Heart, 
  Activity, 
  Flame, 
  Droplet, 
  ShieldCheck, 
  Sparkles, 
  Sun, 
  Smile,
  ArrowRight
} from "lucide-react";

export default function HealthConditions() {
  const navigate = useNavigate();

  const conditions = [
    {
      id: "fullbody",
      title: "Full Body Checkup",
      subtitle: "60+ Vital Parameters",
      icon: <Sparkles size={22} />,
      color: "from-rose-600 to-red-600",
      bgLight: "bg-rose-50 text-rose-700",
      route: "/all-packages",
      badge: "Most Booked"
    },
    {
      id: "diabetes",
      title: "Diabetes Care",
      subtitle: "HbA1c, Fasting Sugar",
      icon: <Droplet size={22} />,
      color: "from-blue-600 to-indigo-600",
      bgLight: "bg-blue-50 text-blue-700",
      route: "/all-tests",
      badge: "Essential"
    },
    {
      id: "heart",
      title: "Heart Health",
      subtitle: "Lipid Profile, ECG",
      icon: <Heart size={22} />,
      color: "from-red-600 to-rose-600",
      bgLight: "bg-red-50 text-red-700",
      route: "/all-tests",
      badge: "Critical"
    },
    {
      id: "thyroid",
      title: "Thyroid Profile",
      subtitle: "T3, T4, TSH Total",
      icon: <Activity size={22} />,
      color: "from-amber-600 to-orange-600",
      bgLight: "bg-amber-50 text-amber-700",
      route: "/all-tests",
      badge: "Popular"
    },
    {
      id: "fever",
      title: "Fever & Infection",
      subtitle: "CBC, Dengue, Malaria",
      icon: <Flame size={22} />,
      color: "from-orange-600 to-amber-600",
      bgLight: "bg-orange-50 text-orange-700",
      route: "/all-tests",
      badge: "Fast Report"
    },
    {
      id: "vitamins",
      title: "Vitamin & Immunity",
      subtitle: "Vit D3, B12, Calcium",
      icon: <Sun size={22} />,
      color: "from-emerald-600 to-teal-600",
      bgLight: "bg-emerald-50 text-emerald-700",
      route: "/all-tests",
      badge: "Top Pick"
    },
    {
      id: "liver-kidney",
      title: "Liver & Kidney",
      subtitle: "LFT, KFT, Creatinine",
      icon: <ShieldCheck size={22} />,
      color: "from-purple-600 to-indigo-600",
      bgLight: "bg-purple-50 text-purple-700",
      route: "/all-tests",
      badge: "Comprehensive"
    },
    {
      id: "women-health",
      title: "Women Health",
      subtitle: "PCOD, Hormones, Iron",
      icon: <Smile size={22} />,
      color: "from-pink-600 to-rose-600",
      bgLight: "bg-pink-50 text-pink-700",
      route: "/all-packages",
      badge: "Specialized"
    }
  ];

  return (
    <div className="w-full py-7 px-4 md:px-8 bg-white rounded-3xl border border-slate-200/80 shadow-xs mt-2">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 pb-4 border-b border-slate-100 gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-600"></span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-red-600">Health Problem Finder</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
            Book Tests by <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">Health Concerns</span>
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-1">
            Find certified diagnostic tests and full body checkups targeted to your symptoms
          </p>
        </div>

        <button
          onClick={() => navigate("/all-tests")}
          className="inline-flex items-center gap-1.5 text-xs md:text-sm font-bold text-red-600 hover:text-red-700 hover:gap-2 transition-all cursor-pointer"
        >
          <span>View All 100+ Tests</span>
          <ArrowRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {conditions.map((item) => (
          <div
            key={item.id}
            onClick={() => navigate(item.route)}
            className="group relative bg-slate-50/70 hover:bg-white p-4 md:p-5 rounded-2xl border border-slate-200/80 hover:border-red-300 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer flex flex-col justify-between"
          >
            {item.badge && (
              <span className={"self-start text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 border border-current/10 " + item.bgLight}>
                {item.badge}
              </span>
            )}

            <div className="flex items-center gap-3 mt-1">
              <div className={"w-12 h-12 rounded-xl bg-gradient-to-br " + item.color + " text-white flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform duration-200"}>
                {item.icon}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xs md:text-sm font-black text-slate-900 group-hover:text-red-600 transition-colors truncate">
                  {item.title}
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5 truncate font-medium">
                  {item.subtitle}
                </p>
              </div>
            </div>

            <div className="mt-3.5 pt-2.5 border-t border-slate-200/60 flex items-center justify-between text-[11px] font-bold text-slate-400 group-hover:text-red-600 transition-colors">
              <span>Explore packages</span>
              <ArrowRight size={13} className="transform group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
